import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import {
  Topic as TopicIcon,
  QuestionAnswer as AnswerIcon,
  Code as TemplateIcon,
  CheckCircleOutline as VerifiedIcon,
} from '@mui/icons-material';
import AnimatedProgress from './AnimatedProgress';

const reviewConfig = [
  {
    key: 'topic_verified',
    label: 'Topic Verification',
    icon: TopicIcon,
    color: 'primary',
    description: 'Topics assigned and verified',
  },
  {
    key: 'answer_verified',
    label: 'Answer Verification',
    icon: AnswerIcon,
    color: 'secondary',
    description: 'AI answers reviewed and approved',
  },
  {
    key: 'template_verified',
    label: 'Template Verification',
    icon: TemplateIcon,
    color: 'info',
    description: 'Question format and structure verified',
  },
];

export default function ReviewMetrics({ metrics }) {
  if (!metrics) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6">Review Metrics</Typography>
          <Typography color="text.secondary">No data available</Typography>
        </CardContent>
      </Card>
    );
  }

  const fullyVerifiedPercentage = metrics.fully_verified?.percentage || 0;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Human Review Status
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Verification progress by review type
        </Typography>

        {/* Overall Progress */}
        <Box sx={{ mt: 3, mb: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <VerifiedIcon sx={{ fontSize: 40, color: 'success.dark' }} />
            </Grid>
            <Grid item xs>
              <Typography variant="subtitle1" color="success.dark">
                Fully Verified
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h4" color="success.dark">
                  {fullyVerifiedPercentage}%
                </Typography>
                <Typography variant="body2" color="success.dark">
                  {metrics.fully_verified?.count || 0} / {metrics.total_segments || 0} segments
                </Typography>
              </Box>
              <AnimatedProgress
                value={fullyVerifiedPercentage}
                height={8}
                color="success"
                animate={true}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Individual Review Types */}
        <Box sx={{ mt: 3 }}>
          {reviewConfig.map((review) => {
            const reviewMetrics = metrics[review.key];
            const Icon = review.icon;
            const percentage = reviewMetrics?.percentage || 0;
            const count = reviewMetrics?.count || 0;
            const total = metrics.total_segments || 0;

            return (
              <Box key={review.key} sx={{ mb: 2 }}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    <Icon 
                      sx={{ 
                        fontSize: 24, 
                        color: `${review.color}.main`,
                        opacity: 0.7 
                      }} 
                    />
                  </Grid>
                  <Grid item xs>
                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2">{review.label}</Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" color="text.secondary">
                            {count} / {total}
                          </Typography>
                          <Chip 
                            label={`${percentage}%`} 
                            size="small" 
                            color={percentage === 100 ? 'success' : 'default'}
                          />
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {review.description}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <AnimatedProgress
                          value={percentage}
                          height={4}
                          color={review.color}
                          animate={true}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </Box>

        {/* Summary */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Eligible for Review:</strong> {metrics.total_segments || 0} segments
          </Typography>
          {fullyVerifiedPercentage < 100 && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              {metrics.total_segments - (metrics.fully_verified?.count || 0)} segments need review
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}